import {
  CardNumberDefaultLabels,
  ExpirationDateDefaultLabels,
  InitialSetupItem,
  SetupItemType,
  StripeDefaultSetup,
  StripeSetupItemTypeName,
  StripeSetupType,
} from "../consts";
import { payment3_paymentmethodpb } from "@ftdr/payment3_paymentmethod_coordinator-js-client";
import { cardStyles } from "../stripe-elements.styles";
import { StripeFormDetails } from "../../stripe-form";
import { CreatePaymentMethodResultType } from "../../../../../../contexts/payments-client-context";
import {
  AgregatedProcessPaymentResponse,
  GeneralStatusValue,
  SuccessTypeValue,
} from "../../../../types";
import { FormsSetupType, StyleType } from "../../../../../../types";
import { PaymentusDefaultSetup } from "../../../../paymentus-iframe";
import { BraintreeDefaultSetup } from "../../../../paypal-processor/const";

type ReturnType = Array<Array<SetupItemType<boolean> | SetupItemType<string>>>;

const REQUIRED_PROPERTIES: StripeSetupItemTypeName[] = [
  "cardNumber",
  "expirationDate",
];

export const generateSetupArray = (
  setup: StripeSetupType = StripeDefaultSetup,
  styleType: StyleType = "default"
): ReturnType => {
  const setupKeys = Object.keys(setup) as Array<keyof typeof setup>;
  if (
    !REQUIRED_PROPERTIES.every((requiredProperty) =>
      setupKeys.includes(requiredProperty)
    )
  ) {
    throw Error("Required properties missing (cardNumber,expirationDate)");
  }

  return setupKeys.reduce<ReturnType>((acc, key) => {
    const defaultSetupItemValue = setup[key]?.defaultValue || "";
    const setupItem: SetupItemType<typeof defaultSetupItemValue> =
      setup[key] || InitialSetupItem;
    acc[setupItem?.row] = [
      ...(acc[setupItem?.row] || []),
      {
        ...setupItem,
        name: key,
        styles: { ...cardStyles[styleType], ...setupItem.styles },
      },
    ];
    return acc;
  }, []);
};

export const generateDefaultValueObject = (
  setup: StripeSetupType = StripeDefaultSetup
): Partial<StripeFormDetails> => {
  const setupKeys = Object.keys(setup) as Array<
    keyof Omit<typeof setup, "cardNumber" | "expirationDate" | "cvc">
  >;
  return setupKeys.reduce<StripeFormDetails>((acc: StripeFormDetails, key) => {
    if (key === "saveInWallet") {
      acc[key] = !!setup[key]?.defaultValue;
    } else {
      acc[key] = setup[key]?.defaultValue || "";
    }
    return acc;
  }, {});
};

export const createResultObject = (
  resultObject: CreatePaymentMethodResultType
): AgregatedProcessPaymentResponse => {
  if (resultObject?.error) {
    return {
      stripe: { ...resultObject.error },
      generalStatus: GeneralStatusValue.PAYMENT_FAILED,
    };
  }
  if (resultObject?.result?.paymentMethod?.details?.cc?.brand) {
    const brandNumber = resultObject.result.paymentMethod.details.cc.brand;
    resultObject.result.paymentMethod.details.cc.brandName =
      payment3_paymentmethodpb.CreditCardBrand[brandNumber];
  }
  return {
    stripe: {
      type: SuccessTypeValue.STRIPE_SUCCESS,
      stripeSuccessResponse: resultObject?.result,
    },
    generalStatus: GeneralStatusValue.PAYMENT_SUCCESS,
  };
};

export const getStripeDefaultSetup = (
  styleType: StyleType = "default"
): StripeSetupType => {
  return {
    cardNumber: {
      row: 0,
      label: CardNumberDefaultLabels[styleType],
      styles: cardStyles[styleType],
      className: "sm:col-start-1 sm:col-span-4 col-start-1 col-end-7 sm:pr-4",
    },
    expirationDate: {
      row: 0,
      label: ExpirationDateDefaultLabels[styleType],
      className: "sm:col-span-2 sm:col-end-7 col-start-1 col-span-7",
      styles: cardStyles[styleType],
    },
  };
};
export const getFormsSetupDefaultValue = (
  styleType: StyleType = "default"
): FormsSetupType => {
  return {
    stripeForm: {
      mode: "onSubmit",
      reValidateMode: "onChange",
      fieldsSetup: getStripeDefaultSetup(styleType),
    },
    paymentusForm: {
      mode: "onSubmit",
      reValidateMode: "onBlur",
      fieldsSetup: PaymentusDefaultSetup,
    },
    braintreeForm: {
      fieldsSetup: BraintreeDefaultSetup,
    },
  };
};
